import { computed, Directive, input } from '@angular/core';

import { buttonEnumInputTransformer, ButtonEnumInputType } from './button-common-transformers';

export enum CommonButtonOutlinedSize {
	XS = 'XS',
	S = 'S',
	M = 'M',
	L = 'L',
}

export enum CommonButtonOutlinedState {
	DEFAULT = 'DEFAULT',
	SELECTED = 'SELECTED',
	DISABLED = 'DISABLED',
}

const CLASSES_BY_SIZE_MAP: Record<CommonButtonOutlinedSize, string[]> = {
	[CommonButtonOutlinedSize.XS]: ['br-0t', 'p-0h-tb', 'p-2-rl'],
	[CommonButtonOutlinedSize.S]: ['br-1', 'p-0t-tb', 'p-2-rl'],
	[CommonButtonOutlinedSize.M]: ['br-1', 'p-1-tb', 'p-2-rl'],
	[CommonButtonOutlinedSize.L]: ['br-1', 'p-1-tb', 'p-3-rl'],
};

const CLASSES_BY_STATE_MAP: Record<CommonButtonOutlinedState, string[]> = {
	[CommonButtonOutlinedState.DEFAULT]: ['border-230', 'fc-800', 'bg-standard-hover-on-hover'],
	[CommonButtonOutlinedState.SELECTED]: ['fc-800', 'bg-primary-20', 'border-primary-20'],
	[CommonButtonOutlinedState.DISABLED]: ['border-250', 'fc-200'],
};

@Directive({
	selector: '[commonButtonOutlined],[commonButtonBordered]',
	standalone: true,
	host: {
		class: 'fl fl-ai-c fl-jc-fs fl-gap-0t bordered',
		'[class]': '$classes()',
		'[attr.data-size]': '$size()',
		'[attr.data-state]': '$state()',
		'[attr.disabled]': '$disabled()',
	},
})
export class CommonButtonOutlinedDirective {
	public readonly $size = input<
		CommonButtonOutlinedSize,
		ButtonEnumInputType<CommonButtonOutlinedSize>
	>(CommonButtonOutlinedSize.M, {
		alias: 'size',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonOutlinedSize),
				CommonButtonOutlinedSize.M
			),
	});

	public readonly $state = input<
		CommonButtonOutlinedState,
		ButtonEnumInputType<CommonButtonOutlinedState>
	>(CommonButtonOutlinedState.DEFAULT, {
		alias: 'state',
		transform: (value) =>
			buttonEnumInputTransformer(
				value,
				Object.values(CommonButtonOutlinedState),
				CommonButtonOutlinedState.DEFAULT
			),
	});

	protected readonly $classes = computed(() => {
		const size = this.$size();
		const state = this.$state();

		const classesBySize = CLASSES_BY_SIZE_MAP[size] ?? [];
		const classesByState = CLASSES_BY_STATE_MAP[state] ?? [];

		const classes = [...classesBySize, ...classesByState];

		return classes.join(' ');
	});

	protected readonly $disabled = computed(() => {
		const state = this.$state();
		return state === CommonButtonOutlinedState.DISABLED;
	});
}
